import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Clock, Lightning } from "@phosphor-icons/react";

// Shown in the header for trial users only. Renders "Day X of 7" plus an
// Upgrade CTA. Goes amber at day 2, red at day 1/0 to create urgency
// right when trial-abuse prevention kicks in. Hidden for paid / starter /
// premium / enterprise / trial_pending users.
export default function TrialCountdown({ user }) {
  const state = useMemo(() => {
    if (!user || user.plan !== "trial" || !user.trial_ends_at) return null;
    const ends = new Date(user.trial_ends_at);
    if (isNaN(ends.getTime())) return null;
    const msLeft = ends.getTime() - Date.now();
    const daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
    const TRIAL_TOTAL = 7;
    // "Day X of 7" counts elapsed days (1-indexed) rather than remaining.
    const elapsed = Math.min(TRIAL_TOTAL, Math.max(1, TRIAL_TOTAL - daysLeft + 1));

    let tone = "calm";
    if (daysLeft <= 1) tone = "danger";
    else if (daysLeft <= 2) tone = "warn";

    return { daysLeft, elapsed, total: TRIAL_TOTAL, tone, expired: msLeft <= 0 };
  }, [user]);

  if (!state) return null;

  const toneClasses = {
    calm:   "bg-[#F5F5F5] border-black/10 text-[#0A0A0A]",
    warn:   "bg-amber-50 border-amber-300 text-amber-900",
    danger: "bg-red-50 border-red-300 text-red-900",
  }[state.tone];

  const dotClasses = {
    calm:   "bg-[#4A7A00]",
    warn:   "bg-amber-500",
    danger: "bg-red-500 animate-pulse",
  }[state.tone];

  const label = state.expired
    ? "Trial ended"
    : `Day ${state.elapsed} of ${state.total}`;

  const subLabel = state.expired
    ? "Pick a plan to keep going"
    : state.daysLeft === 1
      ? "1 day left"
      : `${state.daysLeft} days left`;

  return (
    <Link
      to="/pricing"
      data-testid="trial-countdown"
      className={`hidden md:inline-flex items-center gap-2.5 px-3 py-1.5 border ${toneClasses} hover:opacity-80 transition-opacity rounded-none`}
      title="Upgrade to keep access after your trial ends"
    >
      <span className="flex items-center gap-1.5">
        <span className={`w-1.5 h-1.5 ${dotClasses}`} />
        <Clock size={14} weight="bold" />
        <span className="text-[11px] font-bold uppercase tracking-wider tabular-nums">{label}</span>
      </span>
      <span className="h-3 w-px bg-current opacity-20" />
      <span className="text-[11px] font-medium opacity-75 tabular-nums">{subLabel}</span>
      <span className="flex items-center gap-1 ml-1 text-[11px] font-bold text-[#4A7A00] uppercase tracking-wider">
        <Lightning size={12} weight="fill" /> Upgrade
      </span>
    </Link>
  );
}
